import { createLogger } from '@ondit/logger'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { requestJson } from '@/lib/api/client/request'
import {
  type ConnectedAccount,
  type LinkOAuthAccountBody,
  linkOAuthAccountContract,
  listOAuthConnectionsContract,
  type OAuthAccountSummary,
  type OAuthConnection,
} from '@/lib/api/contracts/oauth-connections'
import { OAUTH_CREDENTIAL_DRAFT_CALLBACK_PARAM } from '@/lib/credentials/draft-constants'
import { OAUTH_PROVIDERS, type OAuthServiceConfig } from '@/lib/oauth'
import { getPerRequestOAuthLinkScopes } from '@/lib/oauth/utils'
import { apiUrl } from '~/core/api/url'

const logger = createLogger('OAuthConnectionsQuery')

export const OAUTH_CONNECTIONS_STALE_TIME = 30 * 1000
export const OAUTH_CONNECTED_ACCOUNTS_STALE_TIME = 60 * 1000

/**
 * Query key factory for OAuth connection queries.
 * Provides hierarchical cache keys for connections and provider-specific accounts.
 */
export const oauthConnectionsKeys = {
  all: ['oauthConnections'] as const,
  connections: () => [...oauthConnectionsKeys.all, 'connections'] as const,
  accounts: () => [...oauthConnectionsKeys.all, 'accounts'] as const,
  account: (provider: string) => [...oauthConnectionsKeys.accounts(), provider] as const,
}

/** OAuth service with connection status and linked accounts. */
export interface ServiceInfo extends OAuthServiceConfig {
  id: string
  isConnected: boolean
  lastConnected?: string
  accounts?: OAuthAccountSummary[]
}

type OAuthConnectionResponse = OAuthConnection

function defineServices(): ServiceInfo[] {
  const servicesList: ServiceInfo[] = []

  Object.entries(OAUTH_PROVIDERS).forEach(([_providerKey, provider]) => {
    Object.entries(provider.services).forEach(([serviceKey, service]) => {
      servicesList.push({
        ...service,
        id: serviceKey,
        isConnected: false,
        scopes: service.scopes || [],
      })
    })
  })

  return servicesList
}

/**
 * Resolves the service catalog merged with the caller's connections.
 *
 * A failed request resolves with the bare catalog rather than rejecting, so
 * consumers keep correct service names and ids when the merge data is
 * unavailable. The cost is that `isConnected`/`accounts` then report *unknown*
 * as *disconnected*, which the result cannot distinguish. Read connection
 * state from the workspace credentials query (`useWorkspaceCredentials`), which
 * surfaces its own errors; a consumer that must branch on `isConnected` here
 * needs this fallback removed first, or it will tell a connected user they are
 * not.
 */
async function fetchOAuthConnections(signal?: AbortSignal): Promise<ServiceInfo[]> {
  try {
    const serviceDefinitions = defineServices()

    const data = await requestJson(listOAuthConnectionsContract, { signal })
    const connections = data.connections || []

    const updatedServices = serviceDefinitions.map((service) => {
      const connection = connections.find(
        (conn: OAuthConnectionResponse) => conn.provider === service.providerId
      )

      if (connection) {
        return {
          ...service,
          isConnected: (connection.accounts?.length ?? 0) > 0,
          accounts: connection.accounts || [],
          lastConnected: connection.lastConnected,
        }
      }

      const connectionWithScopes = connections.find((conn: OAuthConnectionResponse) => {
        if (!conn.baseProvider || !service.providerId.startsWith(conn.baseProvider)) {
          return false
        }

        if (conn.scopes && service.scopes) {
          const connScopes = conn.scopes
          return service.scopes.every((scope) => connScopes.includes(scope))
        }

        return false
      })

      if (connectionWithScopes) {
        return {
          ...service,
          isConnected: (connectionWithScopes.accounts?.length ?? 0) > 0,
          accounts: connectionWithScopes.accounts || [],
          lastConnected: connectionWithScopes.lastConnected,
        }
      }

      return service
    })

    return updatedServices
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return defineServices()
    }
    logger.error('Error fetching OAuth connections:', error)
    return defineServices()
  }
}

/**
 * Fetches all OAuth service connections with their status.
 * Returns service definitions merged with connection data.
 */
export function useOAuthConnections() {
  return useQuery({
    queryKey: oauthConnectionsKeys.connections(),
    queryFn: ({ signal }) => fetchOAuthConnections(signal),
    staleTime: OAUTH_CONNECTIONS_STALE_TIME,
    retry: false,
  })
}

/**
 * Asks the server for a provider's authorize URL, and goes there.
 *
 * What better-auth's `oauth2.link` did, without better-auth: Rust answers the
 * same path with the same `{ url, redirect }`. A refusal throws here, where the
 * client this replaced handed it back as a value the connect flow never read —
 * so a refused connect used to do nothing at all.
 */
export async function startOAuthLink(body: LinkOAuthAccountBody): Promise<void> {
  const { url } = await requestJson(linkOAuthAccountContract, { body })
  window.location.href = apiUrl(url)
}

interface ConnectServiceParams {
  providerId: string
  callbackURL: string
  draftId?: string
  /** The store or environment a tenant provider's URLs are built from. */
  tenant?: string
}

/**
 * Initiates OAuth connection flow for a service.
 * Redirects the user to the provider's authorization page.
 */
export function useConnectOAuthService() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ providerId, callbackURL, draftId, tenant }: ConnectServiceParams) => {
      if (providerId === 'instagram') {
        const returnUrl = encodeURIComponent(callbackURL)
        const draftQuery = draftId ? `&draftId=${encodeURIComponent(draftId)}` : ''
        window.location.href = apiUrl(
          `/api/auth/instagram/authorize?returnUrl=${returnUrl}${draftQuery}`
        )
        return { success: true }
      }

      const stateCallbackUrl = new URL(callbackURL)
      if (draftId) {
        stateCallbackUrl.searchParams.set(OAUTH_CREDENTIAL_DRAFT_CALLBACK_PARAM, draftId)
      }

      const scopes = getPerRequestOAuthLinkScopes(providerId)
      await startOAuthLink({
        providerId,
        callbackURL: stateCallbackUrl.toString(),
        ...(scopes && { scopes }),
        ...(tenant && { tenant }),
      })

      return { success: true }
    },
    onError: (error) => {
      logger.error('OAuth connection error:', error)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: oauthConnectionsKeys.connections() })
    },
  })
}

/** Connected OAuth account for a specific provider. */
export type { ConnectedAccount }
